import React, { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';

const DAYS = [
  { key: 'mon', label: 'Man' },
  { key: 'tue', label: 'Tir' },
  { key: 'wed', label: 'Ons' },
  { key: 'thu', label: 'Tor' },
  { key: 'fri', label: 'Fre' },
  { key: 'sat', label: 'Lør' },
  { key: 'sun', label: 'Søn' },
];

const DEFAULT_HOURS = {
  mon: { open: true, from: '08:00', to: '16:00' },
  tue: { open: true, from: '08:00', to: '16:00' },
  wed: { open: true, from: '08:00', to: '16:00' },
  thu: { open: true, from: '08:00', to: '16:00' },
  fri: { open: true, from: '08:00', to: '16:00' },
  sat: { open: false, from: '09:00', to: '13:00' },
  sun: { open: false, from: '09:00', to: '13:00' },
};

/**
 * Try to parse a free-text business hours string like:
 *   "Man–Fre: 08:00–16:00, Lør: 09:00–13:00"
 * into the structured format. Best-effort: returns null when ambiguous.
 */
function parseHoursString(text) {
  if (!text || typeof text !== 'string') return null;
  // For now, just return a sensible default structure marked as "edited third-partyly"
  return null;
}

/**
 * Serialize structured hours to a human-readable string.
 */
export function serializeHours(hours) {
  const open = DAYS.filter(d => hours[d.key]?.open);
  if (open.length === 0) return 'Stengt';
  // Group consecutive same-time days
  const groups = [];
  let current = null;
  for (const d of DAYS) {
    const h = hours[d.key];
    if (!h || !h.open) {
      if (current) { groups.push(current); current = null; }
      continue;
    }
    if (current && current.from === h.from && current.to === h.to) {
      current.days.push(d.label);
    } else {
      if (current) groups.push(current);
      current = { days: [d.label], from: h.from, to: h.to };
    }
  }
  if (current) groups.push(current);
  return groups.map(g => {
    const range = g.days.length > 1 ? `${g.days[0]}–${g.days[g.days.length - 1]}` : g.days[0];
    return `${range}: ${g.from}–${g.to}`;
  }).join(', ');
}

export default function BusinessHoursEditor({ value, onChange }) {
  const [hours, setHours] = useState(() => {
    const parsed = parseHoursString(value);
    return parsed || DEFAULT_HOURS;
  });
  const [showEditor, setShowEditor] = useState(false);

  useEffect(() => {
    if (showEditor) {
      const serialized = serializeHours(hours);
      onChange?.(serialized);
    }
  }, [hours, showEditor]); // eslint-disable-line

  const updateDay = (day, field, val) => {
    setHours(prev => ({
      ...prev,
      [day]: { ...prev[day], [field]: val },
    }));
  };

  return (
    <div className="space-y-3" data-testid="business-hours-editor">
      <button
        type="button"
        onClick={() => setShowEditor(s => !s)}
        className="text-xs text-accent-blue hover:text-accent-blue-hover flex items-center gap-1 transition-colors"
      >
        <Clock size={12} /> {showEditor ? 'Skjul visuell editor' : 'Bruk visuell editor'}
      </button>

      {showEditor && (
        <div className="bg-navy-900 border border-navy-600 rounded-lg p-4 space-y-2">
          {DAYS.map(({ key, label }) => {
            const h = hours[key] || { open: false, from: '08:00', to: '16:00' };
            return (
              <div key={key} className="flex items-center gap-3">
                <label className="flex items-center gap-2 w-20 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={h.open}
                    onChange={(e) => updateDay(key, 'open', e.target.checked)}
                    className="w-3.5 h-3.5 rounded border-navy-600 bg-navy-800 text-accent-blue focus:ring-accent-blue"
                    data-testid={`bh-${key}-toggle`}
                  />
                  <span className="text-sm text-white">{label}</span>
                </label>
                {h.open ? (
                  <div className="flex items-center gap-2 flex-1">
                    <input
                      type="time"
                      value={h.from}
                      onChange={(e) => updateDay(key, 'from', e.target.value)}
                      className="px-2 py-1 bg-navy-800 border border-navy-600 rounded text-sm text-white focus:outline-none focus:ring-1 focus:ring-accent-blue"
                      data-testid={`bh-${key}-from`}
                    />
                    <span className="text-slate-500 text-sm">–</span>
                    <input
                      type="time"
                      value={h.to}
                      onChange={(e) => updateDay(key, 'to', e.target.value)}
                      className="px-2 py-1 bg-navy-800 border border-navy-600 rounded text-sm text-white focus:outline-none focus:ring-1 focus:ring-accent-blue"
                      data-testid={`bh-${key}-to`}
                    />
                  </div>
                ) : (
                  <span className="text-sm text-slate-500 italic">Stengt</span>
                )}
              </div>
            );
          })}
          <div className="pt-2 mt-2 border-t border-navy-700 text-xs text-slate-500">
            Forhåndsvisning: <span className="text-slate-300">{serializeHours(hours)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
